// src/components/Privacy.jsx

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

export default function Privacy() {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <div style={pageStyle}>
      {/* Modern Navigation */}
      <nav style={navStyle}>
        <div style={navContainerStyle}>
          <span style={navLogoStyle} onClick={() => navigate("/")} role="button">
            Dansy
          </span>
          
          <div style={navButtonsStyle}>
            <button onClick={() => navigate("/")} style={navBackButton}>
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div style={heroStyle}>
        <div style={heroContentStyle}>
          <h1 style={heroTitleStyle} data-aos="fade-down">Privacy Policy</h1>
          <p style={heroSubtitleStyle} data-aos="fade-up">
            Your privacy matters to us. Learn how we protect your data.
          </p>
          <p style={lastUpdatedStyle}>Last updated: November 4, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div style={contentContainerStyle}>
        <div style={contentWrapperStyle} data-aos="fade-up">
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>1. Introduction</h2>
            <p style={paragraphStyle}>
              This Privacy Policy explains how Dansy ("we", "us", or "our") collects, uses, stores, and protects your personal information when you use our choreography management platform.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>2. Information We Collect</h2>
            <p style={paragraphStyle}>
              We collect various types of information when you interact with our platform, including your name, email address, IP address, and other data provided during registration or when using certain features.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>3. How We Use Your Data</h2>
            <p style={paragraphStyle}>
              The collected data is used to improve our service, personalize your experience, provide effective support, and inform you of important updates. We also use this information to analyze platform usage and optimize our offerings.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>4. Data Controller</h2>
            <p style={paragraphStyle}>
              The data controller is Dansy. For any questions regarding the management of your personal data, you can contact us at: <a href="mailto:privacy@dansy.com" style={linkStyle}>privacy@dansy.com</a>.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>5. Data Sharing and Disclosure</h2>
            <p style={paragraphStyle}>
              Your personal information is not shared with third parties without your consent, except as required by law or to fulfill contractual obligations (e.g., with service providers bound by confidentiality obligations).
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>6. Data Security</h2>
            <p style={paragraphStyle}>
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no transmission or storage method is completely secure.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>7. Cookies</h2>
            <p style={paragraphStyle}>
              Our platform uses cookies to remember your preferences, facilitate your navigation, and analyze site usage. You can configure your browser to refuse cookies, although this may affect certain platform functionalities.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>8. Your Rights</h2>
            <p style={paragraphStyle}>
              In accordance with applicable regulations, you have the right to access, rectify, delete, and object to the processing of your personal data. To exercise these rights, please contact us at: <a href="mailto:privacy@dansy.com" style={linkStyle}>privacy@dansy.com</a>.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>9. Data Retention</h2>
            <p style={paragraphStyle}>
              Your personal data is retained for the duration necessary to achieve the purposes for which it was collected. Once these purposes are fulfilled, your data is either deleted or anonymized.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>10. International Data Transfers</h2>
            <p style={paragraphStyle}>
              In some cases, your data may be transferred and stored in countries outside the European Economic Area. Dansy ensures that these transfers are subject to appropriate safeguards.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>11. Changes to Privacy Policy</h2>
            <p style={paragraphStyle}>
              We reserve the right to modify this Privacy Policy at any time. Any changes will be published on this page and take effect immediately. We recommend regularly reviewing this page to stay informed of updates.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>12. Contact</h2>
            <p style={paragraphStyle}>
              For any questions regarding this Privacy Policy or to exercise your rights, please contact us at: <a href="mailto:support@dansy.com" style={linkStyle}>support@dansy.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

/*--------------------------------
  STYLES
---------------------------------*/

const pageStyle = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  backgroundColor: "#fafafa",
  minHeight: "100vh",
};

const navStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
};

const navContainerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '20px 40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const navLogoStyle = {
  fontSize: '1.5rem',
  fontFamily: "'Shirkhand', cursive",
  color: '#ff477e',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
};

const navButtonsStyle = {
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
};

const navBackButton = {
  padding: '10px 24px',
  fontSize: '0.95rem',
  backgroundColor: 'transparent',
  color: '#333',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 500,
  transition: 'background-color 0.2s ease',
};

const heroStyle = {
  background: 'linear-gradient(135deg, #ff477e 0%, #ff6b9d 100%)',
  padding: '140px 40px 80px',
  textAlign: 'center',
  color: 'white',
};

const heroContentStyle = {
  maxWidth: '800px',
  margin: '0 auto',
};

const heroTitleStyle = {
  fontSize: '3.5rem',
  fontWeight: 700,
  marginBottom: '16px',
};

const heroSubtitleStyle = {
  fontSize: '1.25rem',
  marginBottom: '16px',
  opacity: 0.95,
};

const lastUpdatedStyle = {
  fontSize: '0.9rem',
  opacity: 0.8,
};

const contentContainerStyle = {
  maxWidth: '800px',
  margin: '-60px auto 0',
  padding: '0 40px 100px',
  position: 'relative',
  zIndex: 2,
};

const contentWrapperStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '60px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
};

const sectionStyle = {
  marginBottom: '48px',
};

const sectionTitleStyle = {
  fontSize: '1.8rem',
  fontWeight: 700,
  color: '#333',
  marginBottom: '16px',
  color: '#ff477e',
};

const paragraphStyle = {
  fontSize: '1.05rem',
  lineHeight: 1.8,
  color: '#666',
};

const linkStyle = {
  color: '#ff477e',
  textDecoration: 'none',
  fontWeight: 500,
  borderBottom: '1px solid rgba(255, 71, 126, 0.3)',
  transition: 'border-color 0.2s ease',
};
